import { Grid } from "@mui/material";
import { FieldValues, useFormContext, UseFormReturn } from "react-hook-form";
import RhfDateTimePicker from "../../../libs/rhf-mui/RhfDateTimePicker";
import RhfNumberPicker from "../../../libs/rhf-mui/RhfNumberPicker";
import RhfSelect from "../../../libs/rhf-mui/RhfSelect";
import RhfTextBox from "../../../libs/rhf-mui/RhfTextBox";
import { Lang } from "../../dao/SpwkUtil";
import { SpwkUtilV3 } from "../../dao/SpwkUtilV3";
import { UniInput, UniInputKind } from "../../dao/UniInput";
import { InputToken, UniOption } from "../../dao/UniOption";
import { new_ErrorAlert } from "../../export_spwk/ExportManager";
import { useGV } from "../../GV";
import {
  UiMetaTableContextProps,
  useUiMetaTable,
} from "../../pages/FrmSpwkQuestV3/UiMetaTable";
import _ from "lodash";
import { Member } from "../../dao/Member";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

type Props = {
  uni_option: UniOption;
  input_token: InputToken;
  margin_right?: string;

  LangA: Lang;
  LangB: Lang;
};

const UcUniInput = ({
  uni_option,
  input_token,
  margin_right,
  LangA,
  LangB,
}: Props) => {
  const GV = useGV();
  const { t } = useTranslation();

  const DISABLED_COLOR = "#EEEEEE";
  const DEFUALT_INPUT_WIDTH = "175px";

  const DEFUALT_NUM_WIDTH = "100px";

  const input_kind = input_token.InputAttr?.input_kind ?? "";
  let width = input_token.InputAttr?.width ?? "";

  let rhf = useFormContext();

  let ui_meta_context = useUiMetaTable();

  function GetDebugLabel(uni_input: UniInput | undefined, lang_b: Lang) {
    if (uni_input == null) {
      return undefined;
    }

    if (lang_b === SpwkUtilV3.LANG_HIS) {
      return uni_input.i_his_rule + ":" + uni_input.i_his_code;
    } else if (lang_b === SpwkUtilV3.LANG_CODE) {
      const input_code = input_token.input_code;
      return input_code;
    } else if (lang_b === SpwkUtilV3.LANG_RHF_NAME) {
      const rhf_name = uni_option.get_Input_RhfName(input_token.input_code);
      return rhf_name;
    } else if (lang_b === SpwkUtilV3.LANG_GOV) {
      const rhf_name = uni_input.i_gov_code;
      return rhf_name;
    } else {
      return undefined;
    }
  }

  /** 嘗試取得存檔前的數值
   * 目前特作每次都會存檔，似乎不可以使用，要再實驗看看
   */
  function GetOriValue(
    ui_meta_context: UiMetaTableContextProps | null,
    rhf_name: string
  ) {
    if (ui_meta_context == null) {
      return undefined; // 沒有 Context 取不到原始數值
    }

    let ori_rhf_data = ui_meta_context.OriRhfData;
    if (ori_rhf_data == null) {
      return undefined; // 沒有 OriRhfData 取不到原始數值
    }

    let ori_value = _.get(ori_rhf_data, rhf_name); // 嘗試取得存檔前的數值
    return ori_value;
  }

  function render_Input() {
    let IS_ADMIN: boolean = GV.IsAdmin(); // 模擬後台權限

    let ori_value: string | undefined = GetOriValue(ui_meta_context, rhf_name);

    let enable =
      input_token.InputAttr == null
        ? false
        : input_token.InputAttr.IsEnable(IS_ADMIN, ori_value);

    enable = 依照勾選狀態決定Input是否可以輸入(
      ui_meta_context,
      rhf_name,
      uni_option,
      rhf,
      enable
    );
    enable = 依照匯入狀態決定Input是否可以輸入(
      enable,
      rhf_name,
      ui_meta_context?.member,
      IS_ADMIN
    );

    //--------------------------------------------------------------------------
    const debug_label = GetDebugLabel(input_token.InputAttr, LangB);

    if (input_kind === UniInputKind.None) {
      return render_UnknownInput();
    } else {
      const input_attr =
        debug_label == null
          ? undefined
          : { label: debug_label, InputLabelProps: { shrink: true } };

      if (input_kind === UniInputKind.Text) {
        return render_TextBox(input_attr, input_token.InputAttr);
      } else if (input_kind === UniInputKind.Select) {
        return render_DropDown(input_attr);
      } else if (input_kind === UniInputKind.DateTime) {
        return render_DateTimePicker(enable, input_attr);
      } else if (input_kind === UniInputKind.Int) {
        //#region Int 整數
        if (input_token.InputAttr == null) {
          throw new_ErrorAlert("4164351354 應該要有 UniInput !!!");
        }

        let digit =
          input_token.InputAttr.digit === ""
            ? undefined
            : parseInt(input_token.InputAttr.digit);

        if (digit != null && digit !== 0) {
          throw new_ErrorAlert(
            "49466 類型 Int 不應該設定 digit !!!serno=" +
              input_token.InputAttr.serno +
              " " +
              digit
          );
        }

        return render_NumberPicker(
          0,
          input_token.InputAttr,
          enable,
          input_attr
        );
        //#endregion
      } else if (input_kind === UniInputKind.Number) {
        //#region Number 數字
        if (input_token.InputAttr == null) {
          throw new_ErrorAlert("4646133 應該要有 UniInput !!!");
        }

        let digit =
          input_token.InputAttr.digit === ""
            ? undefined
            : parseInt(input_token.InputAttr.digit);
        return render_NumberPicker(
          digit,
          input_token.InputAttr,
          enable,
          input_attr
        );
        //#endregion
      } else {
        return render_UnknownInput();
      }
    }

    function render_DropDown(
      input_attr:
        | { label: string; InputLabelProps: { shrink: boolean } }
        | undefined
    ) {
      if (input_token.InputAttr == null) {
        throw new Error("4132434 InputAttr 不應該是 null !!!");
      }

      let back_color = enable ? undefined : DISABLED_COLOR; // 不可改為灰色
      let items = input_token.InputAttr.getInputItems();

      //#region DropDown
      return (
        <RhfSelect
          name={rhf_name}
          size="small"
          {...input_attr}
          disabled={!enable}
          sx={{
            width: width === "" ? DEFUALT_INPUT_WIDTH : width,
            backgroundColor: back_color,
          }}
          options={items}
        />
      );
      //#endregion
    }

    function render_TextBox(
      input_attr:
        | { label: string; InputLabelProps: { shrink: boolean } }
        | undefined,
      uni_input: UniInput | null | undefined
    ) {
      let back_color = enable ? undefined : DISABLED_COLOR; // 不可改為灰色

      let input_label = uni_input?.input_label ? t(uni_input?.input_label) : "";
      let input_place_holder = uni_input?.place_holder
        ? t(uni_input?.place_holder)
        : undefined;

      if (input_attr == null) {
        input_attr = { label: input_label, InputLabelProps: { shrink: true } };
      }

      (input_attr as any).placeholder = input_place_holder;

      //#region TextBox
      return (
        <RhfTextBox
          size="small"
          name={rhf_name}
          {...input_attr}
          rows={uni_input?.rows}
          disabled={!enable}
          sx={{
            width: width === "" ? DEFUALT_INPUT_WIDTH : width,
            backgroundColor: back_color,
          }}
        />
      );
      //#endregion
    }
  }

  const rhf_name = uni_option.get_Input_RhfName(input_token.input_code);

  return (
    <Grid
      item
      sx={{
        mr: margin_right,
        width: width.endsWith("%") ? "100%" : undefined, // Input 使用百分比時，如 多行TextBox，則 Grid填滿寬度，讓 Input 以百分比顯示
      }}
    >
      {render_Input()}
    </Grid>
  );

  function render_DateTimePicker(
    enable: boolean,
    input_attr:
      | { label: string; InputLabelProps: { shrink: boolean } }
      | undefined
  ) {
    let back_color = enable ? undefined : DISABLED_COLOR; // 不可改為灰色

    //#region DateTime
    let input_format = input_token.InputAttr?.input_format ?? undefined;
    input_format = input_format === "" ? undefined : input_format;

    return (
      <RhfDateTimePicker
        name={rhf_name}
        size="small"
        disabled={!enable}
        sx={{
          width: width === "" ? DEFUALT_INPUT_WIDTH : width,
          backgroundColor: back_color,
        }}
        inputFormat={input_format}
        {...input_attr}
      />
    );
    //#endregion
  }

  function render_NumberPicker(
    digit: number | undefined,
    uni_input: UniInput,
    enable: boolean,
    input_attr: any
  ) {
    let min = uni_input.min_value === "" ? "0" : parseInt(uni_input.min_value); // 未指定最小值，則統一為0
    let max =
      uni_input.max_value === "" ? undefined : parseInt(uni_input.max_value);

    let use_half_mode = true;
    //#region 暫時這樣判斷，如果經常使用 再抽出來
    let step = uni_input.get_step();
    if (step === "0.5") {
      use_half_mode = true;
    } else if (step === "0.1") {
      use_half_mode = false;
    } else {
      throw new_ErrorAlert("13513518 必須是 '0.5' 或 '0.1' ");
    }
    //#endregion

    //#region Int
    return (
      <RhfNumberPicker
        name={rhf_name}
        type="number"
        size="small"
        disabled={!enable}
        use_half_mode={use_half_mode}
        min={min}
        max={max}
        digit={digit}
        sx={{
          // bgcolor: "#FFFFFA",
          bgcolor: !enable ? DISABLED_COLOR : undefined,
          width: width === "" ? DEFUALT_NUM_WIDTH : width,
        }}
        // placeholder={"整數"}
        {...input_attr}
      />
    );
    //#endregion
  }

  function render_UnknownInput() {
    /* 未定義Input格式 */
    return (
      <RhfTextBox
        size="small"
        name={rhf_name}
        sx={{
          // bgcolor: "#FF0000",
          bgcolor: "#FFDDDD",
          width:
            input_token.InputAttr == null
              ? DEFUALT_INPUT_WIDTH
              : input_token.InputAttr?.width === ""
              ? DEFUALT_INPUT_WIDTH
              : input_token.InputAttr?.width,
        }}
        placeholder={"尚未設定 Input !"}
      />
    );
  }
};

export default UcUniInput;

function 依照勾選狀態決定Input是否可以輸入(
  ui_meta_context: UiMetaTableContextProps | null,
  rhf_name: string,
  uni_option: UniOption,
  rhf: UseFormReturn<FieldValues, any>,
  enable: boolean
) {
  if (ui_meta_context != null && ui_meta_context._UiMetaTable != null) {
    let input_metas =
      ui_meta_context._UiMetaTable.FilterMetas_ByRhfName(rhf_name);

    if (input_metas.length === 0) {
      throw new_ErrorAlert("464681 應該要有對應的選項!!!");
    }

    for (const input_meta of input_metas) {
      if (input_meta.option_guid === uni_option.guid) {
        // pass;
      } else {
        continue;
        // throw new_ErrorAlert("4864315 找不到對應的 Input !!!");
      }

      if (input_meta.question_kind === SpwkUtilV3.Q_KIND_多選) {
        let opt_meta = ui_meta_context._UiMetaTable.FindOptionByGuid(
          input_meta.option_guid
        );
        let check = rhf.watch(opt_meta.rhf_name);

        if (check !== true) {
          enable = false;
        }
      } else if (input_meta.question_kind === SpwkUtilV3.Q_KIND_單選) {
        let opt_meta = ui_meta_context._UiMetaTable.FindOptionByGuid(
          input_meta.option_guid
        );
        let question_meta =
          ui_meta_context._UiMetaTable.FindQuestionMeta_ByQuestionGuid(
            input_meta.question_guid
          );
        let opt_value = rhf.watch(question_meta.rhf_name);

        if (opt_value !== opt_meta.option_code) {
          enable = false;
        }
      }
    }
  }

  return enable;
}

function 依照匯入狀態決定Input是否可以輸入(
  enable: boolean,
  rhf_name: string,
  member: Member | undefined,
  IS_ADMIN: boolean
) {
  //把格子打開
  //TODO 之後改成不寫死
  if (
    rhf_name === "基本資料.基本資料●受雇日期●input_1" ||
    rhf_name === SpwkUtilV3.RHF_NAME_目前從事_起始日期
  ) {
    //value為null時將輸入框打開
    if (IS_ADMIN) {
      enable = true;
    } else if (dayjs(member?.start_date).year() === 2999) {
      enable = true;
      //value不為null時將輸入框關起來
    } else {
      enable = false;
    }
  }
  return enable;
}
